import {
  BotIcon,
  UserRoundCheckIcon,
  Workflow as WorkflowIcon,
} from 'lucide-react';

import type { WorkflowStepType } from '@/lib/api/generated/model';

/**
 * Whether the step a task is standing on belongs to an agent or to a person.
 *
 * The same two marks the workflow graph uses — `graphNodes.tsx` picks
 * `UserRoundCheckIcon` for a gate and `BotIcon` for everything else — so a
 * reader who has learned them on the canvas does not have to learn them again
 * here. Reusing the pair rather than choosing a new one is the point: this is
 * the app's existing vocabulary for the distinction, not a new one for
 * Overview.
 *
 * It says something the row did not otherwise carry. "Capture Smoke · Write
 * Files" names a step without saying whether a machine or a human is holding
 * it, and that is the difference between a task that will move on its own and
 * one that will not.
 *
 * Lives in `shared` rather than in one feature because two surfaces now render
 * the same "workflow · step" line — Overview's rows and the workspace task list
 * — and the mark has to mean the same thing on both. A cross-feature sub-path
 * import would have been the alternative, and that is a merge blocker.
 *
 * Nothing for `backlog` and `done`. Those are the board's manufactured ends
 * rather than work anyone does, so a mark on them would imply an owner that
 * does not exist.
 *
 * `linked_workflow` (spec 101) reuses the same third mark the graph and the
 * palette use for it (`Workflow` from lucide-react), for the same reason the
 * other two are shared rather than chosen fresh here.
 */
export function StepKindIcon({ kind }: { kind: WorkflowStepType | null }) {
  if (
    kind !== 'ai_step' &&
    kind !== 'human_gate' &&
    kind !== 'linked_workflow'
  ) {
    return null;
  }

  const Icon =
    kind === 'human_gate'
      ? UserRoundCheckIcon
      : kind === 'linked_workflow'
        ? WorkflowIcon
        : BotIcon;

  return (
    <Icon
      aria-label={
        kind === 'human_gate'
          ? 'Human gate'
          : kind === 'linked_workflow'
            ? 'Sub workflow step'
            : 'Agent step'
      }
      /*
        Sits on the text's optical centre, not on its baseline.

        The icon runs inline inside a sentence — "Capture Smoke · <icon> Write
        Files" — so it has to align to type rather than to a box. An
        `inline-block` rests its *bottom edge* on the baseline, and this icon is
        as tall as the font size (12px on 12px) while the text's cap height is
        only about 0.7em, so it stood roughly a third of its own height proud of
        the letters beside it. `translate-y-px` was reclaiming one pixel of
        three.

        `-0.15em` drops the box so its centre lands on the middle of the cap
        band, which is where a reader expects it. Expressed in `em` so it holds
        if the surrounding text size ever changes.
      */
      className="inline-block size-3 shrink-0 align-[-0.15em]"
      strokeWidth={1.75}
    />
  );
}
